import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePortal } from '../context/PortalContext';
import { useSupportContext } from '../context/SupportContext';
import {
  LayoutDashboard,
  FileText,
  Gavel,
  MessageSquare,
  Upload,
  CheckSquare,
  LogOut,
  TrendingUp,
  Bell,
  Menu,
  X,
  User,
  HelpCircle,
} from 'lucide-react';
import FundEliteBrandMark from '../components/branding/FundEliteBrandMark';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import SupportModeBanner from '../components/platform/SupportModeBanner';

/**
 * Managed Client Portal Layout - Phase 6 + Phase 9 Enhancements
 * 
 * Layout shell for managed clients.
 * View-focused experience with limited actions.
 * Specialist handles most workflow operations.
 * 
 * Phase 9: Premium support banner, improved scroll
 */

const NavItem = ({ icon: Icon, label, to, badge }) => {
  const location = useLocation();
  const isActive = location.pathname.startsWith(to);

  return (
    <Link
      to={to}
      className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all mb-1
        ${isActive 
          ? 'bg-white/15 text-white' 
          : 'text-white/70 hover:bg-white/10 hover:text-white'
        }`}
      data-testid={`managed-nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center gap-3">
        <Icon size={20} strokeWidth={1.5} />
        <span>{label}</span>
      </div>
      {badge && (
        <span className="px-2 py-0.5 text-xs bg-emerald-500 text-white rounded-full">{badge}</span>
      )}
    </Link>
  );
};

const ManagedClientPortalLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { capabilities } = usePortal();
  const { supportContext, isInSupportMode } = useSupportContext();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Phase 9.2: Determine effective client identity for context-aware text
  const effectiveClientName = isInSupportMode 
    ? (supportContext?.effective_user_name || 'Client')
    : null;
  
  const isViewingAsSupport = isInSupportMode && supportContext?.effective_user_id;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Managed client navigation - view-focused, limited actions
  const navItems = [
    { 
      icon: LayoutDashboard, 
      label: 'My Status', 
      to: '/client/dashboard',
    },
    {
      icon: FileText,
      label: 'Credit Report',
      to: '/credit-report',
    },
    {
      icon: Gavel,
      label: 'Dispute Status',
      to: '/client/disputes',
    },
    {
      icon: Upload,
      label: 'Documents',
      to: '/client/documents',
    },
    {
      icon: MessageSquare,
      label: 'Messages',
      to: '/client/messages',
    },
    {
      icon: CheckSquare,
      label: 'Action Items',
      to: '/client/tasks',
    },
    {
      icon: User,
      label: 'My Profile',
      to: '/settings/profile',
    },
    {
      icon: HelpCircle,
      label: 'Help & Support',
      to: '/client/support',
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Support Mode Banner - Premium Glassmorphism */}
      <SupportModeBanner />
      
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-gradient-to-b from-emerald-900 to-stone-800 transform transition-transform duration-300 ease-in-out flex flex-col
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        data-testid="managed-sidebar"
      >
        {/* Logo - Fixed */}
        <div className="p-5 mb-2 flex-shrink-0">
          <Link to="/client/dashboard" className="flex flex-col gap-1" data-testid="managed-sidebar-logo">
            <FundEliteBrandMark
              variant="self-repair"
              mode="logo"
              logoClassName="h-8 w-auto max-w-[168px] object-contain object-left"
            />
            <p className="text-xs text-emerald-300/80">Client Portal</p>
          </Link>
        </div>

        {/* Specialist Info Card */}
        <div className="mx-3 mb-4 p-3 bg-white/10 rounded-lg flex-shrink-0">
          <p className="text-xs text-emerald-200 mb-1">Your Specialist</p>
          <p className="text-sm font-medium text-white">Contact your specialist for questions</p>
        </div>

        {/* Navigation - Scrollable */}
        <nav 
          className="flex-1 px-3 pb-4 overflow-y-auto scrollbar-thin scrollbar-thumb-emerald-600 scrollbar-track-transparent"
          style={{ 
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(37,99,235,0.5) transparent'
          }}
        >
          {navItems.map((item, index) => (
            <NavItem
              key={index}
              icon={item.icon}
              label={item.label}
              to={item.to}
              badge={item.badge}
            />
          ))}
        </nav>

        {/* User Card - Fixed at bottom */}
        <div className="p-4 border-t border-white/10 bg-emerald-900/50 flex-shrink-0">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 border-2 border-white/20">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-white/20 text-white font-semibold">
                {user?.name?.charAt(0) || 'C'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white truncate">{user?.name || 'Client'}</p>
              <p className="text-xs text-white/50 truncate">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="text-white/50 hover:text-white p-2 rounded-lg hover:bg-white/10 transition-colors"
              data-testid="managed-logout-btn"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-[rgba(6,28,20,0.75)] z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Header - Phase 9.2 Context-Aware */}
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-slate-100"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
              <div>
                {isViewingAsSupport ? (
                  <>
                    <h2 className="text-lg font-semibold text-stone-900">Managed Client: {effectiveClientName}</h2>
                    <p className="text-sm text-slate-500">Viewing this client's credit repair progress</p>
                  </>
                ) : (
                  <>
                    <h2 className="text-lg font-semibold text-stone-900">Welcome, {user?.name?.split(' ')[0] || 'there'}!</h2>
                    <p className="text-sm text-slate-500">Your credit repair progress</p>
                  </>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button className="p-2 rounded-lg hover:bg-slate-100 relative">
                <Bell size={20} className="text-slate-600" />
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default ManagedClientPortalLayout;
